'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
const API_BASE = process.env.REACT_APP_REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const STATUS_OPTIONS = [
    { value: 'probation', label: 'Continue Probation' },
    { value: 'confirmed', label: 'Confirm' },
    { value: 'extended', label: 'Extend Probation' },
    { value: 'not_confirmed', label: 'Not Confirmed' },
];
// Public, unauthenticated form reached from a Confirmation mail's action
// button — mirrors SalaryRevisionAction.tsx exactly, adapted to the
// probation confirmation decision shape (status/reason/monthsExtended)
// instead of a percentage/PIP decision.
export default function ConfirmationAction() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const role = searchParams.get('role') || '';
    const sig = searchParams.get('sig') || '';
    const isManagerRole = role === 'manager';
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [status, setStatus] = useState('confirmed');
    const [monthsExtended, setMonthsExtended] = useState('3');
    const [reason, setReason] = useState('');
    useEffect(() => {
        if (!id || !role || !sig) {
            setError('This link is invalid.');
            setLoading(false);
            return;
        }
        fetch(`${API_BASE}/confirmations/${id}/mail-action?role=${encodeURIComponent(role)}&sig=${encodeURIComponent(sig)}`)
            .then((r) => r.json())
            .then((res) => {
            if (!res.success) {
                setError(res.message || 'This link could not be verified.');
                setLoading(false);
                return;
            }
            setContext(res.data);
            setLoading(false);
        })
            .catch(() => { setError('Something went wrong loading this form.'); setLoading(false); });
    }, [id, role, sig]);
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            setError('Please provide a reason.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const body = { role, sig, status, reason };
            if (status === 'extended')
                body.monthsExtended = Number(monthsExtended) || 1;
            const res = await fetch(`${API_BASE}/confirmations/${id}/mail-action`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setError(data.message || 'Failed to submit.');
                setSubmitting(false);
                return;
            }
            setSubmitted(true);
        }
        catch {
            setError('Failed to submit.');
        }
        finally {
            setSubmitting(false);
        }
    };
    if (loading) {
        return _jsx("div", { className: "min-h-screen flex items-center justify-center text-gray-500", children: "Loading\u2026" });
    }
    if (error && !context) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50 text-center px-4", children: _jsx("p", { className: "text-gray-600", children: error }) }));
    }
    if (submitted) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-lime-50 text-center px-4", children: _jsxs("div", { children: [_jsx("h2", { className: "text-2xl font-bold text-lime-800 mb-2", children: "Thank you!" }), _jsx("p", { className: "text-gray-700", children: "Your decision has been recorded." })] }) }));
    }
    if (context && !context.actionable) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50 text-center px-4", children: _jsxs("div", { className: "max-w-sm", children: [_jsx("h2", { className: "text-xl font-bold text-gray-800 mb-2", children: "Already handled" }), _jsxs("p", { className: "text-gray-600", children: ["This request has already moved on (current stage: ", context.stage, ") \u2014 no action is needed from this link."] })] }) }));
    }
    const mgrDecision = context?.managerDecision;
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-b from-slate-50 to-white", children: [_jsx("nav", { className: "bg-slate-800 shadow-xl", children: _jsx("div", { className: "container mx-auto px-6 py-6", children: _jsxs("h1", { className: "text-white text-2xl sm:text-3xl font-bold", children: ["Probation Confirmation \u2014 ", isManagerRole ? 'Manager Recommendation' : 'Management Decision'] }) }) }), _jsxs("div", { className: "container mx-auto px-6 py-10 max-w-xl", children: [_jsxs("div", { className: "bg-white rounded-xl shadow p-6 mb-6 space-y-1.5", children: [_jsx("p", { className: "text-sm text-gray-500", children: "Employee" }), _jsx("p", { className: "text-xl font-bold text-slate-800", children: context?.employeeName }), _jsxs("p", { className: "text-sm text-gray-600", children: [context?.designation, " \u00B7 ", context?.department] }), !isManagerRole && (_jsxs("p", { className: "text-sm text-gray-600 mt-2", children: [_jsx("span", { className: "font-semibold text-gray-500", children: "Reporting Manager:" }), " ", context?.reportingManager || '—'] }))] }), !isManagerRole && mgrDecision?.status && (_jsxs("div", { className: "bg-white rounded-xl shadow p-6 mb-6 space-y-1.5", children: [_jsx("p", { className: "text-sm font-semibold text-gray-700 mb-1", children: "Manager's Recommendation" }), _jsxs("p", { className: "text-sm text-gray-600", children: [STATUS_OPTIONS.find(o => o.value === mgrDecision.status)?.label || mgrDecision.status, mgrDecision.monthsExtended ? ` — ${mgrDecision.monthsExtended} month(s)` : ''] }), mgrDecision.reason && _jsxs("p", { className: "text-sm text-gray-600 italic mt-1", children: ["\"", mgrDecision.reason, "\""] })] })), _jsxs("form", { onSubmit: handleSubmit, className: "bg-white rounded-xl shadow p-6 space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-semibold text-gray-700 mb-1", children: "Decision" }), _jsx("select", { value: status, onChange: (e) => setStatus(e.target.value), className: "w-full border rounded-lg px-3 py-2", children: STATUS_OPTIONS.map(o => _jsx("option", { value: o.value, children: o.label }, o.value)) })] }), status === 'extended' && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-semibold text-gray-700 mb-1", children: "Extend By (months)" }), _jsx("input", { type: "number", min: 1, required: true, value: monthsExtended, onChange: (e) => setMonthsExtended(e.target.value), className: "w-full border rounded-lg px-3 py-2" })] })), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-semibold text-gray-700 mb-1", children: "Remarks" }), _jsx("textarea", { value: reason, onChange: (e) => setReason(e.target.value), rows: 4, placeholder: "Share your remarks\u2026", className: "w-full border rounded-lg px-3 py-2 resize-y" })] }), error && _jsx("p", { className: "text-red-600 text-sm", children: error }), _jsx("button", { type: "submit", disabled: submitting, className: "w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition", children: submitting ? 'Submitting…' : 'Submit Decision' })] })] })] }));
}
